import * as React from 'react';
import {useState} from 'react';

import {AccountEntryModal} from '../../authentication/AccountEntryModal/AccountEntryModal';
import {AccountFinderModal} from '../../authentication/AccountFinderModal/AccountFinderModal';
import {ImpError} from '../../../client/imp-error/imp-error.class';
import {SessionStorageService} from '../../../client/session-storage/session-storage.service';
import {useOrder} from '../../lib/hooks/use-order';
import {UsersService} from '../../../client/users/users.service';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import Button from '../../ui/Buttons/Button';

export interface HeaderAccountInfoProps {
    user: User;
}

export const HeaderAccountInfo = ({user}: HeaderAccountInfoProps) => {
    const [showAccountEntryModal, setShowAccountEntryModal] = useState(false);
    const [showAccountFinderModal, setShowAccountFinderModal] = useState(false);
    const componentName = `HeaderAccountInfo`;
    const sessionStorageService: SessionStorageService = useService(`sessionStorageService`);
    const usersService: UsersService = useService(`usersService`);
    const {order} = useOrder({componentName});

    // Set activeCatalogId
    const [selectedCatalogId, setSelectedCatalogId] = useState(user.activeCatalogId);

    /**
     * Returns text for Account link in header
     */
    const getAccountLinkText = () => {
        if (user.activeAccount) {
            if (user.getActiveAccountType() === `District`) {
                return `District: ${user.activeAccount}`;
            }
            if (user.getActiveAccountType() === `Division`) {
                return `Division: ${user.activeAccount}`;
            }
            return `Account #: ${user.activeAccount}`;
        }
        return `No Account Selected`;
    };

    /**
     * Selects the user's custom catalog
     * @param catalogId
     */
    const selectCatalog = (catalogId: string) => {
        setSelectedCatalogId(catalogId);
        usersService
            .selectCatalog(catalogId)
            .then(() => {
                location.reload();
            })
            .catch((selectCatalogErr: ImpError) => {
                alert(selectCatalogErr.message);
            });
    };

    /**
     * Template
     */
    return (
        <div
            className="print:tw-hidden tw-hidden lg:tw-block tw-text-sm tw-font-normal tw-leading-[1.3]"
            id="HeaderAccountInfo"
        >
            <div className="tw-max-w-[1432px] tw-py-0 tw-px-4 tw-w-full tw-mx-auto">
                <div className="tw-flex tw-flex-wrap tw-leading-4 tw-m-0 tw-py-1">
                    <div className="tw-w-1/4 tw-flex tw-relative">
                        {!user.hasCustomCatalogs() && !user.hasMultipleCatalogs() && (
                            <a
                                className="tw-mt-1"
                                href="/shop-talk"
                            >
                                Shop Talk
                            </a>
                        )}
                        {user.hasCustomCatalogs() && user.hasMultipleCatalogs() && (
                            <>
                                <label
                                    className="tw-inline"
                                    htmlFor="header_catalogSelectDropdown"
                                >
                                    Catalog
                                </label>{' '}
                                <select
                                    id="header_catalogSelectDropdown"
                                    onChange={(event) => {
                                        selectCatalog(event.target.value);
                                    }}
                                    style={{maxWidth: 200}}
                                    value={selectedCatalogId}
                                >
                                    {user.catalogList.catalogs.map((catalog) => (
                                        <option
                                            key={catalog.cid}
                                            value={catalog.cid}
                                        >
                                            {catalog.name}
                                        </option>
                                    ))}
                                </select>
                            </>
                        )}
                    </div>
                    <div className="tw-w-3/4 tw-relative tw-text-right">
                        <div>
                            {user.isLoggedIn() ? (
                                <>
                                    Hello, <span className="maskPII">{user.fullName}</span>&nbsp;-{' '}
                                    <a
                                        href="/account"
                                        id="header_welcome_yourAccount"
                                        title="Your account functions"
                                    >
                                        {getAccountLinkText()}
                                    </a>
                                    {user.canSwitchAccount() && (
                                        <>
                                            {' '}
                                            -{' '}
                                            <Button
                                                className="tw-text-sm tw-font-normal tw-leading-[1.3]"
                                                onClick={() => {
                                                    setShowAccountFinderModal(true);
                                                }}
                                                size="sm"
                                                variant="link"
                                            >
                                                Switch Account
                                            </Button>
                                        </>
                                    )}
                                    {user.isAllAccountAccess() && (
                                        <>
                                            {' '}
                                            -{' '}
                                            <Button
                                                className="tw-text-sm tw-font-normal tw-leading-[1.3]"
                                                onClick={() => {
                                                    setShowAccountEntryModal(true);
                                                }}
                                                size="sm"
                                                variant="link"
                                            >
                                                Enter Account
                                            </Button>
                                        </>
                                    )}
                                    {(order || user.hasActiveOrder()) && (
                                        <>
                                            {' '}
                                            -{' '}
                                            <a
                                                href={`/orders/${order?.orderNbr || user.orderNumber || ''}`}
                                                id="header_welcome_currentOrder"
                                            >
                                                Order #{order?.orderNbr || user.orderNumber || ``}
                                            </a>
                                        </>
                                    )}
                                    {user.hasOrderApprovalAccess && (
                                        <>
                                            {' '}
                                            -{' '}
                                            <a
                                                href="/orders/approval"
                                                id="header_welcome_pendingOrders"
                                                title="Processing pending orders"
                                            >
                                                Order Approval
                                            </a>
                                        </>
                                    )}{' '}
                                    -{' '}
                                    <a
                                        href="/logout"
                                        id="header_welcome_logout"
                                        onClick={() => sessionStorageService.clear()}
                                        title="Log out of your account"
                                    >
                                        Log out
                                    </a>
                                    {user.impersonate && <span className="maskPII">&nbsp;- OW: {user.impersonate}</span>}
                                    {user.isFieldService() && (
                                        <>
                                            &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;<a href="/fs-photo-uploader">Upload Photos</a>
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    <a
                                        href="/create-account"
                                        id="header_welcome_createAccount"
                                        title="Create Account"
                                    >
                                        Create Account
                                    </a>
                                    &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                                    <Button
                                        className="tw-text-sm tw-font-normal tw-leading-[1.3]"
                                        id="header_welcome_login"
                                        onClick={() => usersService.showLoginPopUp()}
                                        size="sm"
                                        variant="link"
                                    >
                                        Log In
                                    </Button>
                                </>
                            )}
                            &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                            <a
                                href="/contact-us"
                                id="headerContactUsLink"
                            >
                                Contact Us
                            </a>
                            {user.hasCustomCatalogs() && user.hasMultipleCatalogs() && (
                                <>
                                    &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                                    <a
                                        className="tw-mt-1"
                                        href="/shop-talk"
                                    >
                                        Shop Talk
                                    </a>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <AccountEntryModal
                    onHide={() => {
                        setShowAccountEntryModal(false);
                    }}
                    show={showAccountEntryModal}
                />
                <AccountFinderModal
                    onHide={() => setShowAccountFinderModal(false)}
                    selectAccountMode={user.useSelectAccountMode()}
                    show={showAccountFinderModal}
                    user={user}
                />
            </div>
        </div>
    );
};
